import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import css from './ProgressBar.css';
import Tooltip from './Tooltip';

const valueSigns = {
    money: '$',
    number: '',
};

function ProgressBar({
    progressSections = [],
    barHeight = '20px',
    barWidth = '100%',
    containerColor = 'var(--gray400)',
    fillColor = 'var(--blue300)',
    range = null,
    valueType = 'number',
    showLegend = false,
    renderLegend = progressPercentage => `${progressPercentage}%`,
    legendColor = 'var(--blue300)',
    highlightedId = null,
}) {
    const progressPercentage = progressSections.reduce((acc, section) => acc + Math.floor(section.percentage), 0);
    const normalizedPercentage = Math.min(100, Math.max(0, progressPercentage));
    const calcTransformForLegend = () => {
        const maxTransform = 100;
        if (normalizedPercentage <= 4) {
            return maxTransform - 10 * normalizedPercentage;
        }
        if (normalizedPercentage >= 96) {
            return 10 * (100 - normalizedPercentage);
        }
        return maxTransform / 2;
    };

    const hasZeroPercentage = progressSections.some(section => section.percentage < 1);
    const formattedProgressSections = progressSections.map(section => ({
        ...section,
        percentage: Math.floor(section.percentage),
        width: `${Math.floor(hasZeroPercentage ? section.percentage + 1 : section.percentage)}%`,
    }));

    const dynamicCSSVariables = {
        '--legend-transform-percent': `${calcTransformForLegend()}%`,
        '--bar-height': barHeight,
        '--bar-width': barWidth,
        '--container-color': containerColor,
        '--fill-color': fillColor,
        '--fill-width': `${normalizedPercentage}%`,
        '--legend-top': `-${parseInt(barHeight, 10) + 8}px`,
        '--legend-color': legendColor,
        '--filler-border-right': formattedProgressSections.length > 1 ? '4px solid white' : 'none',
        '--filler-border-radius': formattedProgressSections.length > 1 ? '0' : '30px',
        '--filler-last-border-radius':
            normalizedPercentage === 100 || formattedProgressSections.length === 1 ? '30px' : '0',
    };
    return (
        <div className={css.progressBarContainer} style={dynamicCSSVariables}>
            {showLegend && (
                <div className={css.legend}>
                    <span className={css.legendText}>{renderLegend(progressPercentage)}</span>
                </div>
            )}
            <div className={css.progressBar}>
                {formattedProgressSections.map(({ width, id, value, percentage, tooltipKey }, index) => {
                    const key = id || index;
                    const style = { width };
                    const wrapperClass = classnames(css.fillerWrapper, {
                        [css.withTooltip]: Boolean(tooltipKey),
                        [css.highlight]: highlightedId === id,
                    });

                    return tooltipKey ? (
                        <Tooltip
                            key={key}
                            titleTranslationKey={tooltipKey}
                            titleTranslationData={{ value: value?.toLocaleString(), percentage }}
                            trigger="mouseenter"
                            style={style}
                            wrapperClass={wrapperClass}
                        >
                            <div className={css.filler} />
                        </Tooltip>
                    ) : (
                        <div className={wrapperClass} style={style} key={key}>
                            <div className={css.filler} />
                        </div>
                    );
                })}
            </div>
            {range?.length === 2 && (
                <div className={css.rangeBar}>
                    <span>{`${range[0] !== 0 ? valueSigns[valueType] : ''}${range[0].toLocaleString()}`}</span>
                    <span>{`${range[1] !== Infinity ? valueSigns[valueType] : ''}${range[1].toLocaleString()}`}</span>
                </div>
            )}
        </div>
    );
}

ProgressBar.propTypes = {
    progressSections: PropTypes.arrayOf(
        PropTypes.shape({
            percentage: PropTypes.number.isRequired,
            id: PropTypes.string,
            value: PropTypes.number,
            tooltipKey: PropTypes.string,
        })
    ),
    barHeight: PropTypes.string,
    containerColor: PropTypes.string,
    fillColor: PropTypes.string,
    barWidth: PropTypes.string,
    range: PropTypes.arrayOf(PropTypes.number),
    valueType: PropTypes.oneOf(['number', 'money']),
    showLegend: PropTypes.bool,
    renderLegend: PropTypes.func,
    legendColor: PropTypes.string,
    highlightedId: PropTypes.string,
};

export default ProgressBar;
